import { SideNavigation, SideNavigationProps } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';
import { match, useHistory, useLocation, useRouteMatch } from 'react-router';

import { constants } from './common/Localization';
import { MatchParam } from './common/types';
import { deepCopy } from './common/util';

const header = { text: constants.HEADER, href: '/' };

const mainNavItems: SideNavigationProps.Item[] = [
    {
        type: 'section',
        text: constants.PORTFOLIOS,
        items: [
            { type: 'link', text: constants.VIEW, href: '/portfolios' },
        ]
    },
];

const portfolioNavItems = (portfolioId: string, tcoAssessmentId: string, notExpandedSections: string[]): SideNavigationProps.Item[] => ([
    {
        type: 'link',
        text: constants.BACK_TO_ALL_PORTFOLIOS,
        href: '/portfolios',
    },
    {
        type: 'divider',
    },
    {
        type: 'section',
        text: constants.PORTFOLIO_DATA,
        defaultExpanded: !notExpandedSections.includes(constants.PORTFOLIO_DATA),
        items: [
            { type: 'link', text: constants.ASSETS, href: `/portfolio/${portfolioId}/management/assets` },
            { type: 'link', text: constants.DEPENDENCIES, href: `/portfolio/${portfolioId}/management/dependencies` },
            { type: 'link', text: constants.DASHBOARDS, href: `/portfolio/${portfolioId}/management/dashboard` },
            { type: 'link', text: constants.PROPERTIES, href: `/portfolio/${portfolioId}/properties`},
        ],
    },
    {
        type: 'section',
        text: constants.IMPORT,
        defaultExpanded: !notExpandedSections.includes(constants.IMPORT),
        items: [
            { type: 'link', text: constants.IMPORT_FROM_FILE, href: `/portfolio/${portfolioId}/management/import-from-file` },
            { type: 'link', text: constants.IMPORT_FROM_ADS, href: `/portfolio/${portfolioId}/management/import-from-ads` },
        ],
    },
    {
        type: 'section',
        text: constants.COLLECT,
        defaultExpanded: !notExpandedSections.includes(constants.COLLECT),
        items: [
            { type: 'link', text: constants.CONTACT_MANAGEMENT, href: `/portfolio/${portfolioId}/management/contact-management` },
            { type: 'link', text: constants.DATA_COLLECTION, href: `/portfolio/${portfolioId}/management/data-collection` },
        ],
    },
    {
        type: 'section',
        text: constants.TCO,
        defaultExpanded: !notExpandedSections.includes(constants.TCO),
        items: [
            { type: 'link', text: constants.TCO_SUMMARY, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/summary` },
            { type: 'link', text: constants.ADJUSTMENTS, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/adjustments`},
            { type: 'link', text: constants.SQL_SERVER_OPTIMIZATION, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/sql-server-optimization` },
            { type: 'link', text: constants.APPLICATION_COST_SUMMARY, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/application-summary` },
            { type: 'link', text: constants.MAINFRAME_SUMMARY, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/mainframe-summary` },
        ],
    },
    {
        type: 'section',
        text: constants.AWS,
        defaultExpanded: !notExpandedSections.includes(constants.AWS),
        items: [
            { type: 'link', text: constants.EC2_RECOMMENDATIONS, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/aws/server-recommendations` },
            { type: 'link', text: constants.RDS_RECOMMENDATIONS, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/aws/database-recommendations` },
            { type: 'link', text: constants.EC2_COST, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/aws/server-costs` },
            { type: 'link', text: constants.RDS_COST, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/aws/database-costs` },
            { type: 'link', text: constants.STORAGE_NETWORK_COST, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/aws/storage-network-costs` },
            { type: 'link', text: constants.IT_LABOR_SUPPORT_COST, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/aws/it-labor-support-costs` },
        ],
    },
    {
        type: 'section',
        text: constants.ON_PREMISES,
        defaultExpanded: !notExpandedSections.includes(constants.ON_PREMISES),
        items: [
            { type: 'link', text: constants.ON_PREM_SERVER_COST, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/on-prem/server-costs` },
            { type: 'link', text: constants.ON_PREM_DATABASE_COST, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/on-prem/database-costs` },
            {
                type: 'link',
                text: constants.ON_PREM_STORAGE_NETWORK_COST,
                href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/on-prem/storage-network-costs`,
            },
            { type: 'link', text: constants.ON_PREM_IT_LABOR_COST, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/on-prem/it-labor-costs` },
        ],
    },
    {
        type: 'section',
        text: constants.MIGRATION_STRATEGY,
        defaultExpanded: !notExpandedSections.includes(constants.MIGRATION_STRATEGY),
        items: [
            { type: 'link', text: constants.MIGRATION_STRATEGY_RECOMMENDATIONS, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/migration-strategy` },
        ],
    },
    {
        type: 'section',
        text: constants.MIGRATION_COST,
        defaultExpanded: !notExpandedSections.includes(constants.MIGRATION_COST),
        items: [
            { type: 'link', text: constants.COST_SUMMARY, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/migration/summary` },
            { type: 'link', text: constants.CASH_FLOW, href: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/migration/cash-flow` },
        ],
    },
    {
        type: 'section',
        text: constants.PLAN,
        defaultExpanded: !notExpandedSections.includes(constants.PLAN),
        items: [
            { type: 'link', text: constants.APPLICATION_PRIORITIZATION, href: `/portfolio/${portfolioId}/planning/prioritization` },
            { type: 'link', text: constants.DEPENDENCY_GROUPS, href: `/portfolio/${portfolioId}/planning/grouping` },
            { type: 'link', text: constants.MIGRATION_WAVES, href: `/portfolio/${portfolioId}/planning/waves` },
        ],
    },
]);

const appCentricNavItems = (portfolioId: string, applicationId: string): SideNavigationProps.Item[] => ([
    {
        type: 'link',
        text: constants.ALL_APPLICATIONS,
        href: `/portfolio/${portfolioId}/management/assets/applications`,
    },
    {
        type: 'section',
        text: applicationId,
        items: [
            { type: 'link', text: constants.OVERVIEW, href: `/portfolio/${portfolioId}/management/application-detail/${applicationId}/overview` },
            { type: 'link', text: constants.RESOURCES, href: `/portfolio/${portfolioId}/management/application-detail/${applicationId}/resources` },
            { type: 'link', text: constants.COST, href: `/portfolio/${portfolioId}/management/application-detail/${applicationId}/cost` },
        ]
    },
]);

const formattedLocation = (uri: string) => {
    if (uri.search(/\/assets\//) > -1 || uri.search(/\/dependencies\//) > -1)
        return uri.substring(0, uri.lastIndexOf('/'));
    return uri;
};

function Navigation(): JSX.Element {

    const [ items, setItems ] = useState(mainNavItems);
    const [ activeHref, setActiveHref ] = useState('');
    // by default all sections will be expanded
    const [ notExpandedSections, setNotExpandedSections ] = useState<string[]>([]);

    const history = useHistory();
    const location = useLocation().pathname;
    const portfolioMatch: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const tcoMatch: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const appDetailMatch: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/management/application-detail/:applicationId',
    });
    const dependencyGroupMatch: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/planning/grouping/:groupId',
    });
    const addAdjustmentsMatch: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId/adjustments/add',
    });
    const editAdjustmentsMatch: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId/adjustments/edit',
    });
    const portfolioId = portfolioMatch?.params?.portfolioId;
    const tcoAssessmentId = tcoMatch?.params?.tcoAssessmentId ?? 'default';
    const applicationId = appDetailMatch?.params?.applicationId;
    const groupId = dependencyGroupMatch?.params?.groupId;

    useEffect(() => {
        if (portfolioId && applicationId) {
            setActiveHref(location);
        } else if (portfolioId && groupId) {
            setActiveHref(`/portfolio/${portfolioId}/planning/grouping`);
        } else if (addAdjustmentsMatch || editAdjustmentsMatch) {
            setActiveHref(`/portfolio/${portfolioId}/tco/${tcoAssessmentId}/adjustments`);
        } else if (portfolioId) {
            setActiveHref(formattedLocation(location));
        } else {
            // setting href empty so we don't show View as highlighted during Portfolio Selection Page
            setActiveHref('');
        }
    }, [portfolioId, applicationId, groupId, location, addAdjustmentsMatch, tcoAssessmentId, editAdjustmentsMatch]);

    useEffect(() => {
        if (portfolioId && applicationId) {
            setItems(appCentricNavItems(portfolioId, applicationId));
        } else if (portfolioId) {
            setItems(portfolioNavItems(portfolioId, tcoAssessmentId, notExpandedSections));
        } else {
            setItems(mainNavItems);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ portfolioId, tcoAssessmentId, applicationId ]);

    /**
     * Add or remove a section from the notExpandedSections list
     * called after a user sets the expanded state of a section
     * @param item 
     * @param expanded 
     */
    const expansionStateChanged = (item: SideNavigationProps.Section | SideNavigationProps.ExpandableLinkGroup, expanded: boolean): void => {
        const sections: string[] = deepCopy(notExpandedSections);
        if (expanded && sections.indexOf(item.text) > -1) {
            sections.splice(sections.indexOf(item.text), 1);
        } else if (!expanded) {
            sections.push(item.text);
        }
        setNotExpandedSections(sections);
    };

    return (
        <SideNavigation
            activeHref={activeHref}
            items={items}
            header={header}
            onFollow={event => {
                event.preventDefault();
                history.push(event.detail.href);
            }}
            onChange={event => expansionStateChanged(event.detail.item, event.detail.expanded)}
        />
    );
}

export default Navigation;
